import { LineStyle } from 'lightweight-charts'
import { toUtcSeconds } from '@/utils/utcTime'
import { formatPriceWithPrecision } from '@/utils/pricePrecision'
import { ZoneFillSeries } from '../patron_ruptura_quantum/ZoneFillSeries.js'
import { QUANTUM_COLORS, ZONE_OPACITY, withAlpha } from '../patron_ruptura_quantum/constants.js'
import { formatPct, RESULT_LABEL } from './panelFormat.js'

/**
 * Caja SL/TP de la ÚLTIMA señal de Orderflow Quantum (WP-OQ-panel-v2,
 * docs/specs/Spec_Orderflow_Panel_Polish_y_Niveles_Chart.md §2).
 *
 * Molde: renderer de patron_ruptura_quantum (mismas líneas, mismas zonas por
 * ZoneFillSeries/ZONE_OPACITY, mismas etiquetas en el eje de precios vía
 * price line invisible con axisLabelVisible). Frontend PURO renderizador:
 * `levels` es EXACTAMENTE el bloque `current_levels` de /stats -- entrada,
 * SL, TPs, estado, vela de entrada y distancias % vienen resueltos del
 * backend; aquí no se calcula ningún nivel ni ninguna distancia.
 *
 * Comportamiento con la señal ya resuelta (state != 'open'): igual que PRQ,
 * los niveles quedan congelados hasta el borde derecho (PRQ no los borra tras
 * el outcome). Diferencia deliberada pedida por la spec: la zona se pinta
 * atenuada y la etiqueta de entrada lleva el resultado (TP1 ✓ / SL ✗ ...).
 */

export const CLOSED_ZONE_DIM = 0.5

const COMMON_LINE_OPTS = {
  lastValueVisible: false,
  priceLineVisible: false,
  crosshairMarkerVisible: false,
  autoscaleInfoProvider: () => null,
}

const isTime = (t) => t != null && t !== '' && !Number.isNaN(t)

/** El bloque solo se pinta si trae todos los niveles finitos y una vela de entrada interpretable. */
export function validLevels(levels) {
  if (!levels || typeof levels !== 'object') return null
  for (const k of ['entry', 'sl', 'tp1', 'tp2', 'tp3']) {
    if (!Number.isFinite(levels[k])) return null
  }
  const entryTime = toUtcSeconds(levels.entry_time)
  if (!isTime(entryTime)) return null
  return { entryTime }
}

function addLevelLine(api, color, width, style, price, from, to) {
  const points = from === to
    ? [{ time: from, value: price }]
    : [{ time: from, value: price }, { time: to, value: price }]
  const s = api.addSeries(c => c.addLineSeries({ color, lineWidth: width, lineStyle: style, ...COMMON_LINE_OPTS }))
  if (s) s.setData(points)
}

function addZone(api, chart, span, upper, lower, fillColor) {
  if (!(upper > lower)) return
  const s = api.addCustomSeries(new ZoneFillSeries(), {
    color: 'rgba(0,0,0,0)', fillColor, lineWidth: 0, ...COMMON_LINE_OPTS,
  })
  if (!s) return
  try {
    s.setData(span.map(b => ({ time: b.time, upper, lower, color: fillColor })))
  } catch {
    // Nunca dejar una custom series vacía visible en el chart (mismo criterio que PRQ).
    try { chart?.removeSeries?.(s) } catch { /* noop */ }
  }
}

export function renderLevels(chart, candleSeries, levels, api, precision) {
  const ok = validLevels(levels)
  if (!ok || !api) return
  const { entryTime } = ok
  const { entry, sl, tp1, tp2, tp3, state } = levels
  const closed = state != null && state !== 'open'

  const bars = candleSeries && typeof candleSeries.data === 'function' ? candleSeries.data() : []
  const lastBar = Array.isArray(bars) && bars.length > 0 ? bars[bars.length - 1] : null
  const lastTime = toUtcSeconds(lastBar?.time)
  const endTime = isTime(lastTime) && lastTime > entryTime ? lastTime : entryTime

  // ── Zonas: entrada↔SL (riesgo) y entrada↔TP3 (beneficio), como PRQ ──────
  const span = Array.isArray(bars)
    ? bars.filter(b => isTime(toUtcSeconds(b?.time)) && toUtcSeconds(b.time) >= entryTime).map(b => ({ time: toUtcSeconds(b.time) }))
    : []
  if (span.length >= 2) {
    const alpha = closed ? ZONE_OPACITY * CLOSED_ZONE_DIM : ZONE_OPACITY
    addZone(api, chart, span, Math.max(entry, sl), Math.min(entry, sl), withAlpha(QUANTUM_COLORS.bear, alpha))
    addZone(api, chart, span, Math.max(entry, tp3), Math.min(entry, tp3), withAlpha(QUANTUM_COLORS.bull, alpha))
  }

  // ── Líneas desde la vela de entrada hasta el borde derecho ──────────────
  addLevelLine(api, QUANTUM_COLORS.neutral, 1, LineStyle.Solid, entry, entryTime, endTime)
  addLevelLine(api, QUANTUM_COLORS.bear, 1.5, LineStyle.Solid, sl, entryTime, endTime)
  addLevelLine(api, QUANTUM_COLORS.bull, 1.5, LineStyle.Solid, tp1, entryTime, endTime)
  addLevelLine(api, QUANTUM_COLORS.bull, 1.5, LineStyle.Dashed, tp2, entryTime, endTime)
  addLevelLine(api, QUANTUM_COLORS.bull, 1.5, LineStyle.LargeDashed, tp3, entryTime, endTime)

  // ── Etiquetas en la columna de precio (price line invisible, patrón PRQ) ─
  const px = (v) => formatPriceWithPrecision(v, precision)
  const pct = (key) => {
    const s = formatPct(levels.pct?.[key], { comma: false })
    return s ? ` (${s})` : ''
  }
  const axisLabel = (price, title, color) => api.addPriceLine({ price, title, color, lineVisible: false, axisLabelVisible: true })
  const entryTitle = `Entrada ${px(entry)}${closed && RESULT_LABEL[state] ? ` · ${RESULT_LABEL[state]}` : ''}`
  axisLabel(entry, entryTitle, QUANTUM_COLORS.neutral)
  axisLabel(sl, `SL ${px(sl)}${pct('sl')}`, QUANTUM_COLORS.bear)
  axisLabel(tp1, `TP1 ${px(tp1)}${pct('tp1')}`, QUANTUM_COLORS.bull)
  axisLabel(tp2, `TP2 ${px(tp2)}${pct('tp2')}`, QUANTUM_COLORS.bull)
  axisLabel(tp3, `TP3 ${px(tp3)}${pct('tp3')}`, QUANTUM_COLORS.bull)
}
